import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type { 
  CommsContextType, 
  Component, 
  Draft, 
  Campaign, 
  ComponentFilters,
  CampaignOptions,
  SendOptions,
  LiveUpdate 
} from '../types';
import { 
  componentsService, 
  draftsService, 
  campaignsService,
  webSocketService 
} from '../services/api';

// Communications state
interface CommsState {
  components: Component[];
  drafts: Draft[];
  campaigns: Campaign[];
  currentDraft: Draft | null;
  isLoading: boolean;
  error: string | null;
}

type CommsAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_COMPONENTS'; payload: Component[] }
  | { type: 'ADD_COMPONENT'; payload: Component }
  | { type: 'UPDATE_COMPONENT'; payload: Component }
  | { type: 'REMOVE_COMPONENT'; payload: string }
  | { type: 'SET_DRAFTS'; payload: Draft[] }
  | { type: 'ADD_DRAFT'; payload: Draft }
  | { type: 'UPDATE_DRAFT'; payload: Draft }
  | { type: 'REMOVE_DRAFT'; payload: string }
  | { type: 'SET_CURRENT_DRAFT'; payload: Draft | null }
  | { type: 'SET_CAMPAIGNS'; payload: Campaign[] }
  | { type: 'ADD_CAMPAIGN'; payload: Campaign }
  | { type: 'UPDATE_CAMPAIGN'; payload: Campaign }
  | { type: 'REAL_TIME_UPDATE'; payload: LiveUpdate };

const initialState: CommsState = {
  components: [],
  drafts: [],
  campaigns: [],
  currentDraft: null,
  isLoading: false,
  error: null
};

// Communications reducer
const commsReducer = (state: CommsState, action: CommsAction): CommsState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
      
    case 'SET_COMPONENTS':
      return { ...state, components: action.payload };
      
    case 'ADD_COMPONENT':
      return { 
        ...state, 
        components: [...state.components, action.payload] 
      };
      
    case 'UPDATE_COMPONENT':
      return {
        ...state,
        components: state.components.map(c =>
          c.id === action.payload.id ? action.payload : c
        )
      };
      
    case 'REMOVE_COMPONENT':
      return {
        ...state,
        components: state.components.filter(c => c.id !== action.payload)
      };
      
    case 'SET_DRAFTS':
      return { ...state, drafts: action.payload };
      
    case 'ADD_DRAFT':
      return { 
        ...state, 
        drafts: [...state.drafts, action.payload] 
      };
      
    case 'UPDATE_DRAFT':
      return {
        ...state,
        drafts: state.drafts.map(d =>
          d.id === action.payload.id ? action.payload : d
        ),
        currentDraft: state.currentDraft?.id === action.payload.id 
          ? action.payload 
          : state.currentDraft
      };
      
    case 'REMOVE_DRAFT':
      return {
        ...state,
        drafts: state.drafts.filter(d => d.id !== action.payload),
        currentDraft: state.currentDraft?.id === action.payload 
          ? null 
          : state.currentDraft
      };
      
    case 'SET_CURRENT_DRAFT':
      return { ...state, currentDraft: action.payload };
      
    case 'SET_CAMPAIGNS':
      return { ...state, campaigns: action.payload };
      
    case 'ADD_CAMPAIGN':
      return { 
        ...state, 
        campaigns: [...state.campaigns, action.payload] 
      };
      
    case 'UPDATE_CAMPAIGN':
      return {
        ...state,
        campaigns: state.campaigns.map(c =>
          c.id === action.payload.id ? action.payload : c
        )
      };
      
    case 'REAL_TIME_UPDATE':
      // Handle real-time updates based on type
      return state; // For now, just return current state
      
    default:
      return state;
  }
};

// Create context
const CommsContext = createContext<CommsContextType | undefined>(undefined);

// Communications provider component
export const CommsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(commsReducer, initialState);

  // Initialize data on mount
  useEffect(() => {
    initializeData();
    setupWebSocketConnection();
    
    return () => {
      webSocketService.disconnect();
    };
  }, []);

  const initializeData = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Load initial data in parallel
      const [componentsResponse, draftsResponse, campaignsResponse] = await Promise.all([
        componentsService.getComponents(),
        draftsService.getDrafts(),
        campaignsService.getCampaigns()
      ]);
      
      if (componentsResponse.success) {
        dispatch({ type: 'SET_COMPONENTS', payload: componentsResponse.data });
      }
      
      if (draftsResponse.success) {
        dispatch({ type: 'SET_DRAFTS', payload: draftsResponse.data });
      }
      
      if (campaignsResponse.success) {
        dispatch({ type: 'SET_CAMPAIGNS', payload: campaignsResponse.data });
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to load data' 
      });
    }
  };

  const setupWebSocketConnection = () => {
    webSocketService.connect('/ws/comms');
    
    webSocketService.subscribe('component_updates', (update) => {
      dispatch({ type: 'REAL_TIME_UPDATE', payload: update });
    });
    
    webSocketService.subscribe('draft_updates', (update) => {
      dispatch({ type: 'REAL_TIME_UPDATE', payload: update });
    });
    
    webSocketService.subscribe('campaign_updates', (update) => {
      dispatch({ type: 'REAL_TIME_UPDATE', payload: update });
    });
  };

  // Component actions
  const createComponent = useCallback(async (data: Partial<Component>): Promise<Component> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await componentsService.createComponent(data);
      
      if (response.success) {
        dispatch({ type: 'ADD_COMPONENT', payload: response.data });
        dispatch({ type: 'SET_LOADING', payload: false });
        return response.data;
      } else {
        throw new Error('Failed to create component');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to create component' 
      });
      throw error;
    }
  }, []);

  const updateComponent = useCallback(async (id: string, updates: Partial<Component>): Promise<Component> => {
    try {
      const response = await componentsService.updateComponent(id, updates);
      
      if (response.success) {
        dispatch({ type: 'UPDATE_COMPONENT', payload: response.data });
        return response.data;
      } else {
        throw new Error('Failed to update component');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to update component' 
      });
      throw error;
    }
  }, []);

  const deleteComponent = useCallback(async (id: string): Promise<void> => {
    try {
      await componentsService.deleteComponent(id);
      dispatch({ type: 'REMOVE_COMPONENT', payload: id });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to delete component' 
      });
      throw error;
    }
  }, []);

  const searchComponents = useCallback(async (filters: ComponentFilters): Promise<Component[]> => {
    try {
      const response = await componentsService.getComponents(filters);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to search components');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to search components' 
      });
      throw error;
    }
  }, []);

  // Draft actions
  const createDraft = useCallback(async (data: Partial<Draft>): Promise<Draft> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await draftsService.createDraft(data);
      
      if (response.success) {
        dispatch({ type: 'ADD_DRAFT', payload: response.data });
        dispatch({ type: 'SET_LOADING', payload: false });
        return response.data;
      } else {
        throw new Error('Failed to create draft');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to create draft' 
      });
      throw error;
    }
  }, []);

  const updateDraft = useCallback(async (id: string, updates: Partial<Draft>): Promise<Draft> => {
    try {
      const response = await draftsService.updateDraft(id, updates);
      
      if (response.success) {
        dispatch({ type: 'UPDATE_DRAFT', payload: response.data });
        return response.data;
      } else {
        throw new Error('Failed to update draft');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to update draft' 
      });
      throw error;
    }
  }, []);

  const deleteDraft = useCallback(async (id: string): Promise<void> => {
    try {
      await draftsService.deleteDraft(id);
      dispatch({ type: 'REMOVE_DRAFT', payload: id });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to delete draft' 
      });
      throw error;
    }
  }, []);

  const duplicateDraft = useCallback(async (id: string): Promise<Draft> => {
    try {
      const response = await draftsService.duplicateDraft(id);
      
      if (response.success) {
        dispatch({ type: 'ADD_DRAFT', payload: response.data });
        return response.data;
      } else {
        throw new Error('Failed to duplicate draft');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to duplicate draft' 
      });
      throw error;
    }
  }, []);

  // Campaign actions
  const createCampaign = useCallback(async (draftId: string, options: CampaignOptions): Promise<Campaign> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await campaignsService.createCampaign(draftId, options);
      
      if (response.success) {
        dispatch({ type: 'ADD_CAMPAIGN', payload: response.data });
        dispatch({ type: 'SET_LOADING', payload: false });
        return response.data;
      } else {
        throw new Error('Failed to create campaign');
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to create campaign' 
      });
      throw error;
    }
  }, []);

  const sendCampaign = useCallback(async (campaignId: string, options?: SendOptions): Promise<void> => {
    try {
      await campaignsService.sendCampaign(campaignId, options);
      
      // Update campaign status
      const updatedCampaign = state.campaigns.find(c => c.id === campaignId);
      if (updatedCampaign) {
        dispatch({ 
          type: 'UPDATE_CAMPAIGN', 
          payload: { ...updatedCampaign, status: 'active' }
        });
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to send campaign' 
      });
      throw error;
    }
  }, [state.campaigns]);

  const pauseCampaign = useCallback(async (campaignId: string): Promise<void> => {
    try {
      await campaignsService.pauseCampaign(campaignId);
      
      const updatedCampaign = state.campaigns.find(c => c.id === campaignId);
      if (updatedCampaign) {
        dispatch({ 
          type: 'UPDATE_CAMPAIGN', 
          payload: { ...updatedCampaign, status: 'paused' }
        });
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to pause campaign' 
      });
      throw error;
    }
  }, [state.campaigns]);

  const resumeCampaign = useCallback(async (campaignId: string): Promise<void> => {
    try {
      await campaignsService.resumeCampaign(campaignId);
      
      const updatedCampaign = state.campaigns.find(c => c.id === campaignId);
      if (updatedCampaign) {
        dispatch({ 
          type: 'UPDATE_CAMPAIGN', 
          payload: { ...updatedCampaign, status: 'active' }
        });
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to resume campaign' 
      });
      throw error;
    }
  }, [state.campaigns]);

  // Real-time subscriptions
  const subscribeToUpdates = useCallback((callback: (update: LiveUpdate) => void) => {
    webSocketService.subscribe('live_updates', callback);
  }, []);

  const unsubscribeFromUpdates = useCallback(() => {
    webSocketService.unsubscribe('live_updates');
  }, []);

  const contextValue: CommsContextType = {
    components: state.components,
    drafts: state.drafts,
    campaigns: state.campaigns,
    currentDraft: state.currentDraft,
    isLoading: state.isLoading,
    error: state.error,
    
    createComponent,
    updateComponent,
    deleteComponent,
    searchComponents,
    
    createDraft,
    updateDraft,
    deleteDraft,
    duplicateDraft,
    
    createCampaign,
    sendCampaign,
    pauseCampaign,
    resumeCampaign,
    
    subscribeToUpdates,
    unsubscribeFromUpdates
  };

  return (
    <CommsContext.Provider value={contextValue}>
      {children}
    </CommsContext.Provider>
  );
};

// Custom hook to use communications context
export const useComms = (): CommsContextType => {
  const context = useContext(CommsContext);
  if (context === undefined) {
    throw new Error('useComms must be used within a CommsProvider');
  }
  return context;
};